/**
 * mail_createDraft Command
 * 
 * Create a new email draft from EntitySidebar
 * Opens in a window and adds to folder if in folder context
 */

import { SimpleDocCommand } from '../../../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../../types/commands';
import useEntityStore from '../../../../stores/entityStore';
import { useCommandRegistryStore } from '../../../../stores/commandRegistryStore';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';

export const mailCreateDraftCommand: SimpleDocCommand = {
  id: 'mail_createDraft',
  logicalName: 'mail_createDraft',
  version: 'v1',
  
  description: `Create a new email draft entity.

**USAGE:**
Creates a Mail.Draft entity that opens in MailApp for composition.

**EXAMPLES:**

Create draft:
{
  "title": "New Email"
}

Create in folder:
{
  "title": "Team Update",
  "folderId": "folder_abc"
}`,
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Draft title/subject (defaults to "New Email")',
        required: false,
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder ID to create draft in',
        required: false,
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: { title: 'New Email' },
        expectedOutput: createSuccessResult(
          'Created draft: New Email',
          { 
            entityId: 'draft_123',
            name: 'New Email'
          }
        ),
        description: 'Create new draft',
      },
      {
        input: { 
          title: 'Team Update',
          folderId: 'folder_abc'
        },
        expectedOutput: createSuccessResult(
          'Created draft: Team Update',
          { 
            entityId: 'draft_456',
            name: 'Team Update'
          }
        ),
        description: 'Create draft in folder',
      },
    ],
    bad: [],
  },
  
  formatViewData: (result: any, toolInput: any) => {
    if (result.status === 'success') {
      return {
        type: 'mail_create',
        summary: result.message,
        data: result.data
      };
    }
    return null;
  },
  
  handler: async (
    params: { title?: string; spaceId?: string; folderId?: string },
    context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      const title = params.title || 'New Email';
      const spaceId = params.spaceId || context.spaceId;
      const folderId = params.folderId;
      
      console.log('[MailApp:CreateDraft] Creating draft', { title, spaceId, folderId });
      
      // Get first Gmail integration (or create without integration for now)
      const entityState = useEntityStore.getState();
      const integration = entityState.entities.find(
        (e: any) => (e.entityType === 'Integration' || e.entityType?.startsWith?.('Integration.')) &&
                   ['Gmail', 'Outlook', 'IMAP'].includes(e.skeleton?.integrationType)
      );
      
      const integrationId = integration?._id;
      
      // Create draft entity
      const draft = await entityState.actions.createEntity({
        entityType: 'Mail.Draft',
        name: title,
        spaceId: spaceId,
        skeleton: {
          '@type': 'Mail.Draft',
          integrationId: integrationId || '', // Empty if no integration (can still draft!)
          to: [],
          bodyPlain: '',
          lastEditedAt: new Date().toISOString(),
          autoSaveVersion: 1,
        },
      });
      
      const draftId = draft._id || draft.id;
      console.log('[MailApp:CreateDraft] Draft entity created:', draftId);
      
      // If in a folder, add to folder
      if (folderId && draftId) {
        console.log('[MailApp:CreateDraft] Adding draft to folder:', folderId);
        
        const folder = entityState.entities.find((e: any) => e._id === folderId);
        if (folder) {
          const currentChildEntities = folder.skeleton?.childEntities || [];
          
          // Update folder
          await entityState.actions.updateEntity(folderId, {
            skeleton: { childEntities: [...currentChildEntities, draftId] },
          });
          
          // Update draft
          await entityState.actions.updateEntity(draftId, {
            skeleton: { groupIds: [folderId] },
          });
          
          console.log('[MailApp:CreateDraft] Added to folder successfully');
        }
      }
      
      // Open in window
      const { executeCommand } = useCommandRegistryStore.getState();
      await executeCommand('space_openEntity', {
        entity: draft,
      });
      
      console.log('[MailApp:CreateDraft] ✅ Draft created and opened');
      
      return createSuccessResult(
        `Created draft: ${title}`,
        {
          entityId: draftId,
          llmId: draft.llmId,
          name: title,
        }
      );
    } catch (error) {
      console.error('[MailApp:CreateDraft] ❌ Error:', error);
      return createErrorResult(
        'Failed to create draft',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};

// Register in entity creation registry for EntitySidebar
entityCreationRegistry.register({
  commandId: 'mail_createDraft',
  entityType: 'Mail.Draft',
  displayName: 'Email Draft',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'Envelope',
    phosphorWeight: 'duotone',
  },
  description: 'Create a new email draft',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'productivity',
  sortOrder: 10,
  shortcut: '⌘⇧M',
});
